
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { fetchSites } from "@/services/sitesApi";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import HeroSection from "@/components/HeroSection";
import SitesSection from "@/components/sites/SitesSection";
import SEO from "@/components/SEO";
import { departements } from "@/components/location-bennes/LocationData";
import { departmentSEOData } from "@/components/sites/departmentSEOData";
import RecentReviews from "@/components/RecentReviews";

const Sites = () => {
  const [searchParams] = useSearchParams();
  const deptParam = searchParams.get('dept');
  const { toast } = useToast();

  // Fetch sites data using React Query
  const {
    data: sites = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['sites'],
    queryFn: fetchSites
  });

  // Filter sites by department if dept param exists
  const filteredSites = deptParam 
    ? sites.filter(site => site.postalCode.startsWith(deptParam)) 
    : sites;

  // Handle error state
  if (error) {
    toast({
      title: "Erreur de chargement",
      description: "Impossible de charger les sites. Veuillez réessayer plus tard.",
      variant: "destructive"
    });
  }

  // Find department info based on dept parameter
  const currentDept = deptParam ? departements.find(dept => dept.code === deptParam) : null;
  const isDeptPage = !!currentDept;
  
  // Get department name for SEO
  const deptName = currentDept?.name || '';
  
  // Get the appropriate SEO content for the page title and description
  const seoContent = deptParam && departmentSEOData[deptParam] 
    ? departmentSEOData[deptParam]
    : { title: "", services: [], zones: [] };

  // SEO optimization based on context
  const pageTitle = isDeptPage 
    ? `${seoContent.title || `Sites de recyclage ${deptName}`} - Groupe Fabre Environnement`
    : "Nos Sites de Recyclage et Centres de Gestion des Déchets - Groupe Fabre Environnement";

  const pageDescription = isDeptPage 
    ? `Découvrez nos ${filteredSites.length} centres de recyclage dans ${deptName} (${deptParam}). Solutions complètes : location de bennes, achat de métaux, valorisation des déchets. Devis gratuit.`
    : `${filteredSites.length} sites spécialisés dans le recyclage et la gestion des déchets. Location de bennes, achat de métaux, valorisation. Trouvez le centre le plus proche de chez vous.`;

  const canonicalUrl = isDeptPage ? `/sites?dept=${deptParam}` : "/sites";
  
  return (
    <>
      <SEO 
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        keywords={isDeptPage 
          ? `recyclage ${deptName}, gestion déchets ${deptParam}, location bennes ${deptName}, achat métaux ${deptName}, valorisation déchets ${deptParam}`
          : "recyclage, gestion déchets, location bennes, achat métaux, valorisation déchets, centres recyclage France"
        }
        ogType="website"
        ogImage="https://lovable.dev/opengraph-image-p98pqg.png"
        localBusiness={isDeptPage ? {
          region: deptName,
          postalCode: `${deptParam}000`,
          serviceType: "Gestion des déchets et recyclage"
        } : undefined}
      />

      <Navbar />

      {/* Hero Section optimisé */}
      <HeroSection 
        title={isDeptPage ? `NOS SITES ${deptName.toUpperCase()}` : "NOS SITES DE RECYCLAGE"}
        subtitle={isDeptPage 
          ? `${filteredSites.length} centres spécialisés dans ${deptName} pour tous vos besoins en gestion des déchets`
          : `${filteredSites.length} centres de recyclage à votre service dans toute la France`
        }
        backgroundImage="https://images.unsplash.com/photo-1605810230434-7631ac76ec81?w=1280&q=80" 
        height="h-[400px]"
      />

      {/* Sites Content Section */}
      <SitesSection 
        isDeptPage={isDeptPage}
        deptParam={deptParam}
        deptName={deptName}
        filteredSites={filteredSites}
        isLoading={isLoading}
      />

      {/* Avis clients récents */}
      <RecentReviews />

      <Footer />
    </>
  );
};

export default Sites;
